import { Component } from 'react';
import * as PropTypes from 'prop-types';

import BEM from 'lib/bem';
import { Button, LabeledContainer, InputError } from '@schibsted-svp/react-ui';
import { TimeInput } from 'components/ui/Form/TimeInput';
import TrimConfirmationDialog from './TrimConfirmationDialog';

import './Playback.scss';
import { PrepareClipDownload } from '../PrepareClipDownload/PrepareClipDownload';
import { ClipCreate } from '../ClipCreate/ClipCreate';

const bem = new BEM('Playback');

export default class Playback extends Component {
    static propTypes = {
        assetId: PropTypes.number,
        provider: PropTypes.string,
        assetStreamType: PropTypes.string,
        className: PropTypes.string,
        disabled: PropTypes.bool,
        duration: PropTypes.number,
        onChange: PropTypes.func,
        onClipCreate: PropTypes.func,
        onClipDownload: PropTypes.func,
        clipDownloadDisabled: PropTypes.bool,
        clipDownloadLoading: PropTypes.bool,
        clipDownloadRangeErrors: PropTypes.shape({
            startError: PropTypes.string,
            endError: PropTypes.string,
        }),
        onVideoTrim: PropTypes.func,
        onResetTrim: PropTypes.func,
        value: PropTypes.shape({
            begin: PropTypes.number,
            end: PropTypes.number,
        }),
        precise: PropTypes.bool,
        player: PropTypes.shape({
            currentTime: PropTypes.number,
        }),
        createClipLoading: PropTypes.bool,
        trimLoading: PropTypes.bool,
        trimDisabled: PropTypes.bool,
        resetTrimLoading: PropTypes.bool,
    };

    static defaultProps = {
        className: undefined,
        disabled: false,
        duration: 0,
        onChange: () => {},
        onClipCreate: undefined,
        onClipDownload: undefined,
        clipDownloadDisabled: false,
        clipDownloadLoading: false,
        clipDownloadRangeErrors: undefined,
        onVideoTrim: undefined,
        onResetTrim: undefined,
        value: null,
        precise: false,
        player: null,
        createClipLoading: false,
        trimLoading: false,
        trimDisabled: false,
        resetTrimLoading: false,
    };

    /**
     * @type {Object}
     * @property {{begin: Number, end: Number}} playback
     * @property {{startError: String, endError: String}} clipDownloadRangeErrors
     */
    state = {
        playback: {
            begin: 0,
            end: null,
        },
        clipDownloadRangeErrors: {
            startError: null,
            endError: null,
        },
    };

    componentDidMount() {
        this.resetPlaybackTimes();
    }

    componentDidUpdate(prevProps) {
        const { duration: oldDuration, clipDownloadRangeErrors: oldClipDownloadRangeErrors } = prevProps;
        const { duration: newDuration, clipDownloadRangeErrors: newClipDownloadRangeErrors } = this.props;
        if (oldDuration !== newDuration) {
            this.resetPlaybackTimes();
        }

        if (oldClipDownloadRangeErrors !== newClipDownloadRangeErrors) {
            this.setState((prevState) => ({
                ...prevState,
                clipDownloadRangeErrors: {
                    ...prevState.clipDownloadRangeErrors,
                    ...newClipDownloadRangeErrors,
                },
            }));
        }
    }

    /**
     * @returns {{begin: Number, end: Number}}
     */
    get playback() {
        const { precise, duration } = this.props;
        const { playback } = this.state;

        if (precise) {
            if (playback.end === duration) {
                return { begin: playback.begin, end: null };
            }
            return playback;
        }

        if (playback.end === duration) {
            return { begin: Math.floor(playback.begin), end: null };
        }

        return {
            begin: Math.floor(playback.begin),
            end: playback.end ? Math.floor(playback.end) : null,
        };
    }

    handlePlaybackBeginChange = (begin) => {
        this.setState((prevState) => ({
            clipDownloadRangeErrors: {
                ...prevState.clipDownloadRangeErrors,
                startError: null,
                endError: null,
            },
        }));

        return this.changePlayback({ begin });
    };

    handlePlaybackBeginSet = () => this.changePlayback({ begin: this.props.player.currentTime });

    handlePlaybackEndChange = (end) => {
        this.setState((prevState) => ({
            clipDownloadRangeErrors: {
                ...prevState.clipDownloadRangeErrors,
                startError: null,
                endError: null,
            },
        }));

        return this.changePlayback({ end });
    };

    handlePlaybackEndSet = () => this.changePlayback({ end: this.props.player.currentTime });

    handleCreateClipClick = (isLiveArchive = false, liveArchiveExpiration = undefined) => {
        const { onClipCreate } = this.props;
        onClipCreate(this.playback, isLiveArchive, liveArchiveExpiration);
    };

    handleDownloadClipClick = () => {
        const { onClipDownload } = this.props;
        onClipDownload(this.playback);
    };

    handleVideoTrimClick = () => {
        const { onVideoTrim } = this.props;
        onVideoTrim(this.playback);
    };

    handleResetTrim = () => {
        const { onResetTrim } = this.props;
        onResetTrim();
    };

    resetPlaybackTimes() {
        this.setState(() => ({
            playback: {
                begin: this.props.value?.begin || 0,
                end: this.props.value?.end || null,
            },
        }));
    }

    /**
     * @param {{begin?: Number, end?: Number}} changes
     */
    changePlayback(changes) {
        this.setState(
            ({ playback }) => ({
                playback: { ...playback, ...changes },
            }),
            () => {
                const { onChange, value, duration } = this.props;
                const { playback } = this.state;
                // do not update null value if playback has default values (prevent unsaved form changes)
                if (value === null && playback.begin === 0 && [0, duration].includes(playback.end)) {
                    return;
                }
                onChange(playback);
            }
        );
    }

    /**
     * Render component
     * @return {JSXElement}
     */
    render() {
        const {
            assetId,
            provider,
            assetStreamType,
            className,
            disabled,
            duration,
            onClipCreate,
            onClipDownload,
            clipDownloadDisabled,
            clipDownloadLoading,
            onVideoTrim,
            onResetTrim,
            player,
            createClipLoading,
            trimLoading,
            trimDisabled,
            resetTrimLoading,
        } = this.props;
        const { playback, clipDownloadRangeErrors } = this.state;

        const formDisabled = disabled || !player;

        return (
            <div className={bem.block(undefined, className)}>
                <LabeledContainer strict label="Start time" className={bem.element('input')}>
                    <TimeInput
                        className={clipDownloadRangeErrors.startError != null ? bem.element('error-border') : undefined}
                        size="small"
                        value={playback.begin}
                        maxValue={duration}
                        disabled={formDisabled}
                        onValueChange={this.handlePlaybackBeginChange}
                    />
                </LabeledContainer>

                <LabeledContainer>
                    <Button
                        type="button"
                        size="small"
                        variant="standard"
                        disabled={formDisabled}
                        onClick={this.handlePlaybackBeginSet}
                    >
                        Set start time
                    </Button>
                </LabeledContainer>

                <LabeledContainer strict label="End time" className={bem.element('input')}>
                    <TimeInput
                        className={clipDownloadRangeErrors.endError != null ? bem.element('error-border') : undefined}
                        size="small"
                        value={playback.end}
                        maxValue={duration}
                        disabled={formDisabled}
                        onValueChange={this.handlePlaybackEndChange}
                    />
                </LabeledContainer>

                <LabeledContainer>
                    <Button
                        type="button"
                        size="small"
                        variant="standard"
                        disabled={formDisabled}
                        onClick={this.handlePlaybackEndSet}
                    >
                        Set end time
                    </Button>
                </LabeledContainer>

                {onClipCreate && (
                    <ClipCreate
                        type="button"
                        size="small"
                        assetId={assetId}
                        provider={provider}
                        disabled={formDisabled}
                        onClick={this.handleCreateClipClick}
                        loading={createClipLoading}
                    />
                )}

                {onVideoTrim && (
                    <TrimConfirmationDialog
                        heading="Trim video"
                        message="Are you sure to trim the video?"
                        onConfirmClick={this.handleVideoTrimClick}
                        loading={trimLoading}
                        disabled={trimDisabled || formDisabled}
                        gaTrackedValue="confirm/trim-video"
                    />
                )}
                {onResetTrim && (
                    <TrimConfirmationDialog
                        heading="Reset trim"
                        message="Are you sure to revert the trimmed video back?"
                        onConfirmClick={this.handleResetTrim}
                        loading={resetTrimLoading}
                        disabled={trimDisabled || formDisabled}
                        gaTrackedValue="confirm/reset-trim-video"
                    />
                )}

                {onClipDownload && (
                    <PrepareClipDownload
                        type="button"
                        size="small"
                        assetId={assetId}
                        provider={provider}
                        assetStreamType={assetStreamType}
                        disabled={clipDownloadDisabled || formDisabled}
                        onClick={this.handleDownloadClipClick}
                        loading={clipDownloadLoading}
                    />
                )}

                <div className={bem.element('warning')}>
                    {clipDownloadRangeErrors.startError != null ? (
                        <InputError>{clipDownloadRangeErrors.startError}</InputError>
                    ) : undefined}

                    {clipDownloadRangeErrors.endError != null ? (
                        <InputError>{clipDownloadRangeErrors.endError}</InputError>
                    ) : undefined}
                </div>
            </div>
        );
    }
}
